import { TaskState } from '@schema';
import { TaskName } from '@/common/constants/taskNames.constants';

type State = {
  locations: Record<string, unknown>,
  nodes: Array<Record<string, unknown>>,
  vips: Array<Record<string, unknown>>,
  taskId: ID,
  computeTaskId: ID
};

const initialState: State = {
  locations: {},
  nodes: [],
  vips: [],
  taskId: '',
  computeTaskId: ''
};

const haGetters: CommonGetterTree<State> = {
  getItems: (state, _getters, _rootState, rootGetters) => state.nodes.map(({ id }) => rootGetters['nodes/getItemById'](id)).filter(Boolean),
  getNodesCount: state => state.nodes.length,
  selfserviceLocations: state => state.locations.selfService || [],
  vips: state => state.vips,
  clusterLocations: state => state.locations.haCluster,
  virtualIps: (state, _getters, _rootState, rootGetters) => state.vips.map(vip => {
    const roleSet = rootGetters['rolesets/getRolesSetById'](vip.roles_set);

    return {
      ip: vip.ip,
      network: roleSet?.name,
      roles: roleSet?.roles.join(', ')
    };
  })
};

const haMutations: CommonMutationTree<State> = {
  SET_HA_NODES(state, nodes = []) {
    state.nodes = nodes;
  },
  SET_HA_VIPS(state, vips = []) {
    state.vips = vips;
  },
  SET_LOCATIONS(state, locations) {
    state.locations = locations;
  },
  SET_TASK(state, taskId) {
    state.taskId = taskId;
  },
  SET_COMPUTE_HA_TASK(state, taskId) {
    state.computeTaskId = taskId;
  }
};

const haActions: CommonActionTree<State> = {
  FETCH_COMPUTE_CONFIG({ commit }) {
    return this.$api.get('/api/v2/compute/cluster/ha')
      .then((res) => {
        commit('SET_COMPUTE_HA_TASK', res.data.task_id || '');
        return res;
      });
  },
  FETCH_CONFIG({ commit }) {
    return this.$api.get('/api/v2/ha/configs')
      .then((res) => {
        const { data } = res;
        if (data) {
          commit('SET_TASK', data.task_id || '');
          commit('SET_HA_NODES', data.nodes);
          commit('SET_HA_VIPS', data.virtual_ips);
          commit('SET_LOCATIONS', {
            primaryNode: data.primary_node_location,
            selfService: data.ha_selfservice_location,
            haCluster: data.ha_cluster_location,
          });
        }
        return res;
      });
  },
  UPDATE({ commit }, body) {
    return this.$api.patch('/api/v2/ha/configs', body)
      .then((res) => {
        // eslint-disable-next-line camelcase
        if (res.data?.task_id) {
          commit('SET_TASK', res.data.task_id);
        }
        return res;
      });
  },
  CREATE({ commit }, body) {
    return this.$api.post('/api/v2/ha/configs', body)
      .then((res) => {
        // eslint-disable-next-line camelcase
        if (res.data?.task_id) {
          commit('SET_TASK', res.data.task_id);
        }
        return res;
      });
  },
  DELETE({ commit }) {
    return this.$api.delete('/api/v2/ha/configs')
      .then((res) => {
        // eslint-disable-next-line camelcase
        if (res.data?.task_id) {
          commit('SET_TASK', res.data.task_id);
        }
        return res;
      });
  },
  ADD_NODE({ commit }, body) {
    return this.$api.patch('/api/v2/ha/nodes', body)
      .then((res) => {
        // eslint-disable-next-line camelcase
        if (res.data?.task_id) {
          commit('SET_TASK', res.data.task_id);
        }
        return res;
      });
  },
  REMOVE_NODE({ commit }, nodeId) {
    return this.$api.delete('/api/v2/ha/nodes', {
      nodes: [nodeId],
      force: false
    }).then((res) => {
      // eslint-disable-next-line camelcase
      if (res.data?.task_id) {
        commit('SET_TASK', res.data.task_id);
      }
      return res;
    });
  },
  SWITCH_TO_MASTER(_ctx, id) {
    return this.$api.post('/api/v2/ha/switch-master', { id })
      .then((res) => res).catch((err) => {
        if (err) {
          throw err;
        }
      });
  },
  socket_tasks({ commit }, { name, state, result }) {
    const pendingTasks = [
      TaskName.HA_CREATING,
      TaskName.HA_REPLACING_NODE,
      TaskName.HA_ADDING_NODE,
      TaskName.HA_REMOVING_NODE
    ];
    if (pendingTasks.includes(name) && state === TaskState.SUCCESS) {
      commit('SET_HA_NODES', result.nodes);
      commit('SET_HA_VIPS', result.virtual_ips);
      commit('SET_LOCATIONS', {
        primaryNode: result.primary_node_location,
        selfService: result.ha_selfservice_location,
        haCluster: result.ha_cluster_location,
      });
    }
    if (name === TaskName.HA_DELETING && state === TaskState.SUCCESS) {
      commit('SET_HA_NODES', []);
    }
  }
};

export default new class implements CommonStore<State, unknown> {
  name = 'ha';
  state = initialState;
  getters = haGetters;
  mutations = haMutations;
  actions = haActions;
}();
